import { useEffect, useRef, useState } from "react";

export default function StudioHeroSection() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Open fullscreen modal (single click)
  const openModal = () => {
    setIsOpen(true);

    // optional: pause inline video when opening modal
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  // Close fullscreen modal
  const closeModal = () => {
    setIsOpen(false);
  };

  // ESC key + scroll lock
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <section className="py-6 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* LEFT SIDE */}
          <div>
            <h1 className="text-4xl font-bold text-[#00889D] mb-6">
              Hot Yoga & Fitness Studio
            </h1>

            <p className="text-base text-gray-600 mb-6 leading-relaxed">
              Our studio is conveniently located just steps away from the
              Willowbrook Shopping Centre. Our modern, welcoming studio offers
              a tranquil space to recharge and strengthen your body through hot
              yoga and fitness classes. With easy access and free parking, it's
              the perfect spot to unwind, move, and connect.
            </p>

            <div className="space-y-6">

              {/* LOCATION */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-[#00889D] mb-1">
                    Studio Location
                  </h3>
                  <p className="text-gray-600">
                    105-19925 Willowbrook Dr., Langley
                  </p>
                  <p className="text-gray-600">
                    Located near Willowbrook Shopping Centre
                  </p>
                </div>
              </div>

              {/* PHONE */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-[#00889D] mb-1">Phone</h3>
                  <p className="text-gray-600">(778) 237-7790</p>
                </div>
              </div>

              {/* EMAIL */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-[#00889D] mb-1">Email</h3>
                  <p className="text-gray-600">info@yogaseven.com</p>
                </div>
              </div>

              {/* HOURS */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-[#00889D] mb-1">
                    Studio Hours
                  </h3>
                  <p className="text-gray-600">
                    Monday - Friday: 6:00 AM - 9:00 PM
                  </p>
                  <p className="text-gray-600">
                    Saturday - Sunday: 7:00 AM - 7:00 PM
                  </p>
                </div>
              </div>

              {/* SOCIALS */}
              <div className="flex items-center space-x-4 pt-4">
                <span className="font-semibold text-[#00889D]">
                  Follow Us
                </span>

                <div className="flex space-x-3">

                  <a
                    href="https://www.instagram.com/yogasevenstudio?igsh=MWtsY3lhd2piZW5hcg%3D%3D&utm_source=qr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-[#14B6A4] rounded-full flex items-center justify-center text-white hover:bg-[#0d9488] transition-colors"
                  >
                    <i className="ri-instagram-line"></i>
                  </a>

                  <a
                    href="https://www.facebook.com/profile.php?id=61585312964707"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-[#14B6A4] rounded-full flex items-center justify-center text-white hover:bg-[#0d9488] transition-colors"
                  >
                    <i className="ri-facebook-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE VIDEO */}
          <div className="relative flex justify-center">
            <div
              className="rounded-2xl shadow-2xl overflow-hidden max-w-md w-full cursor-pointer"
              onClick={openModal}
            >
              <video
                ref={videoRef}
                src="https://res.cloudinary.com/dg758eb9e/video/upload/v1779756631/Yoga_7_Promo_video_nwzc8t.mov"
                poster="https://res.cloudinary.com/dg758eb9e/image/upload/v1779762662/494824bb-f773-478f-b380-987ddc58a116.png"
                muted
                loop
                playsInline
                className="w-full h-full object-cover object-[50%_65%]"
              />
            </div>
          </div>

        </div>
      </div>

      {/* FULLSCREEN MODAL */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
          {/* Close button */}
          <button
            onClick={closeModal}
            className="absolute top-5 right-5 text-white text-3xl font-bold"
          >
            ✕
          </button>

          <video
            src="https://res.cloudinary.com/dg758eb9e/video/upload/v1779756631/Yoga_7_Promo_video_nwzc8t.mov"
            poster="https://res.cloudinary.com/dg758eb9e/image/upload/v1779762662/494824bb-f773-478f-b380-987ddc58a116.png"
            controls
            autoPlay
            className="max-h-[90vh] max-w-[90vw] rounded-lg"
          />
        </div>
      )}
    </section>
  );
}